import type { CSSProperties, KeyboardEvent, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";

// The site logo: a miniature of the face-down card back beside the
// stacked "DECK OF / INFINITY" wordmark (the "Card Badge" lockup).
// Used by the app header, the login form, and the landing page
// header. The pack-opening art keeps its plain-text brand line by
// design — packs are product art, not site chrome.

const theme = pixelTheme;

/** The exact infinity path from the face-down card back
 *  (components/Card.tsx) and the favicon. */
const INF_PATH =
  "M 25 25 C 25 5, 50 5, 50 25 C 50 45, 75 45, 75 25 C 75 5, 50 5, 50 25 C 50 45, 25 45, 25 25 Z";

/** A miniature replica of the face-down card back: ink face, plum
 *  border, parchment inner frame, amber infinity. */
function MiniCardBack({ w }: { w: number }) {
  const h = Math.round((w * 7) / 5);
  return (
    <div
      aria-hidden="true"
      style={{
        position: "relative",
        width: w,
        height: h,
        background: E32.ink,
        border: `2px solid ${E32.plum}`,
        flexShrink: 0,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 2,
          border: `1px solid ${E32.parchment}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          viewBox="0 0 100 50"
          width={w * 0.62}
          height={w * 0.31}
          fill="none"
          stroke={E32.amber}
          strokeWidth={10}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ display: "block" }}
        >
          <path d={INF_PATH} />
        </svg>
      </div>
    </div>
  );
}

/** The Deck of Infinity logo lockup. `s` scales the whole lockup;
 *  s = 1 is app-header size (~31px tall). The `InDev!` splash hangs
 *  off the bottom-right corner during the alpha (see `InDevBanner`);
 *  pass `indev={false}` to suppress it where it'd be redundant. */
export function Logo({
  s = 1,
  style,
  indev = true,
}: {
  s?: number;
  style?: CSSProperties;
  indev?: boolean;
}) {
  const line: CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: 10 * s,
    letterSpacing: theme.letterSpacing,
    color: theme.ink,
    lineHeight: 1,
    whiteSpace: "nowrap",
  };
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 8 * s,
        ...style,
      }}
    >
      <MiniCardBack w={22 * s} />
      <div style={{ display: "flex", flexDirection: "column", gap: 3 * s }}>
        <span style={line}>DECK OF</span>
        <span style={line}>INFINITY</span>
      </div>
      {indev && <InDevBanner s={s} />}
    </div>
  );
}

// Minecraft-style "splash text": a tilted, pixel, pulsing yellow
// "InDev!" tag that hangs off the bottom-right corner of the logo and
// reads as a link (underlined). Clicking it routes to the In
// Development page that explains the alpha and its progress resets.
//
// It renders as a `role="link"` <span> rather than an <a>/<Link> on
// purpose: the app header nests the whole logo inside a "go to lobby"
// <Link>, and an anchor-in-anchor is invalid HTML. A span is valid
// non-interactive content there, and `stopPropagation` keeps a click
// on the splash from also triggering the parent lobby link.
const INDEV_ANIM_ID = "indev-banner-styles";

// The static part of the transform (position + tilt) lives in the CSS
// class so it survives `prefers-reduced-motion` turning the pulse off;
// the keyframes only animate the extra scale on top of it.
const INDEV_CSS = `
@keyframes indev-banner-pulse {
  0%, 100% { transform: translate(34%, 58%) rotate(-18deg) scale(1); }
  50%      { transform: translate(34%, 58%) rotate(-18deg) scale(1.14); }
}
.indev-banner {
  position: absolute;
  right: 0;
  bottom: 0;
  transform: translate(34%, 58%) rotate(-18deg);
  transform-origin: center;
  color: ${E32.butter};
  text-decoration: underline;
  text-underline-offset: 2px;
  text-shadow: 1px 1px 0 ${E32.ink};
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  animation: indev-banner-pulse 1.5s ease-in-out infinite;
}
.indev-banner:hover { color: ${E32.white}; }
@media (prefers-reduced-motion: reduce) {
  .indev-banner { animation: none; }
}
`;

if (
  typeof document !== "undefined" &&
  !document.getElementById(INDEV_ANIM_ID)
) {
  const el = document.createElement("style");
  el.id = INDEV_ANIM_ID;
  el.textContent = INDEV_CSS;
  document.head.appendChild(el);
}

function InDevBanner({ s }: { s: number }) {
  const navigate = useNavigate();

  const go = (e: MouseEvent | KeyboardEvent) => {
    // Don't let the click bubble to a parent logo link (e.g. the app
    // header's "go to lobby" wrapper).
    e.preventDefault();
    e.stopPropagation();
    navigate("/in-development");
  };

  return (
    <span
      className="indev-banner"
      role="link"
      tabIndex={0}
      aria-label="In development — what this means"
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") go(e);
      }}
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: Math.max(7, Math.round(7.5 * s)),
        letterSpacing: 0,
        lineHeight: 1,
      }}
    >
      InDev!
    </span>
  );
}
